import { createRoot } from 'react-dom/client';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@styles/index.less';
import zhCN from 'antd/locale/zh_CN';
import { ConfigProvider, App as AntdApp } from 'antd';
import '@ant-design/v5-patch-for-react-19';
import { AppRouter } from '@/router';
import { BASE_PATH } from '@/common/basePath';
import { singleRoutePrefix } from '@/common/routePrefix';
import { getEnvFaviconHref } from '@/common/envLogo';

// 双前缀 URL（网关会再前置一段）能取到 index.html，但 router 只剥一层 basename → 整页 404。
// 先归一到单前缀再挂载路由；归一后立即 reload，因此这次不渲染。
const normalized = singleRoutePrefix(window.location.pathname, BASE_PATH);
if (normalized !== window.location.pathname) {
  window.location.replace(`${normalized}${window.location.search}${window.location.hash}`);
}

// test/dev 环境把浏览器标签页 favicon 也换色,防止多标签页时误把测试环境当成 prod
const faviconLink = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
if (faviconLink) faviconLink.href = getEnvFaviconHref();

const linearTheme = {
  token: {
    colorPrimary: '#5e6ad2',
    colorBgContainer: '#ffffff',
    colorBgLayout: '#f5f5f5',
    colorBgElevated: '#ffffff',
    colorBorder: '#e0e0e0',
    colorBorderSecondary: '#ebebeb',
    colorText: '#1d1d1f',
    colorTextSecondary: '#6b6f76',
    colorTextTertiary: '#9b9da3',
    borderRadius: 6,
    fontFamily:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
    fontSize: 14,
    controlHeight: 32,
    wireframe: false,
  },
  components: {
    Table: {
      headerBg: '#fafafa',
      headerColor: '#6b6f76',
      headerSplitColor: '#ebebeb',
      rowHoverBg: '#f8f8fa',
      borderColor: '#ebebeb',
      cellPaddingBlock: 10,
      cellPaddingInline: 12,
      headerBorderRadius: 0,
    },
    Button: {
      primaryShadow: 'none',
      defaultShadow: 'none',
      dangerShadow: 'none',
      fontWeight: 500,
    },
    Input: {
      activeShadow: '0 0 0 2px rgba(94,106,210,0.12)',
    },
    Select: {
      optionSelectedBg: '#f0f0ff',
    },
    Modal: {
      titleFontSize: 16,
      headerBg: '#ffffff',
      contentBg: '#ffffff',
    },
    Form: {
      labelColor: '#6b6f76',
      labelFontSize: 14,
    },
    Card: {
      boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)',
    },
    Tag: {
      borderRadiusSM: 4,
    },
  },
};

createRoot(document.getElementById('root')!).render(
  <ConfigProvider locale={zhCN} theme={linearTheme}>
    <AntdApp>
      <AppRouter />
    </AntdApp>
  </ConfigProvider>
);
